import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  Renderer2,
  Inject,
  OnInit,
  OnDestroy,
  HostListener
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppIconComponent } from '../icon/icon.component';
import {
  RealAssistService,
  StreamingMessage
} from '../../_services/real-assist.service';
import { MarkdownPipe } from './markdown.pipe';

export interface RealAssistQuestion {
  text: string;
}

export interface ChatActionButton {
  label: string;
  value: string;
}

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  actionButtons?: ChatActionButton[];
  isGreeting?: boolean;
  responseId?: string;
  feedbackGiven?: 'positive' | 'negative' | null;
}

@Component({
  selector: 'app-real-assist',
  standalone: true,
  imports: [CommonModule, FormsModule, AppIconComponent, MarkdownPipe],
  templateUrl: './real-assist.component.html',
  styleUrls: ['./real-assist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RealAssistComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  @Input() variant: 'chat' | 'widget' = 'chat';
  @Input() suggestedQuestions: RealAssistQuestion[] = [
    { text: 'How can I make a quick payment without logging in?' },
    {
      text: 'What is the difference between board members and my management company?'
    },
    { text: 'How can I register for an account?' },
    { text: 'How can I reset my password?' },
    { text: 'How can I log in with Google?' }
  ];
  @Input() placeholder: string = 'Ask me anything';
  @Input() feedbackIconColor: string = '#767676';

  @Output() questionSubmitted = new EventEmitter<string>();
  @Output() suggestedQuestionClicked = new EventEmitter<RealAssistQuestion>();

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('modalMessagesContainer') modalMessagesContainer!: ElementRef;
  @ViewChild('widgetInput') widgetInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('modalInput') modalInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('modalOverlay') modalOverlay!: ElementRef<HTMLDivElement>;

  inputValue: string = '';
  messages: ChatMessage[] = [];
  isTyping: boolean = false;
  chatStarted: boolean = false;
  isExpanded: boolean = false;
  isModalOpen: boolean = false;

  private shouldScrollToBottom = false;
  private blurTimeout: ReturnType<typeof setTimeout> | null = null;
  private copyTimeout: ReturnType<typeof setTimeout> | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private streamingSubscription: Subscription | null = null;
  private currentStreamingMessage: ChatMessage | null = null;
  copiedMessageId: string | null = null;

  // Feedback modal state
  isFeedbackModalOpen: boolean = false;
  feedbackComment: string = '';
  feedbackRating: 'positive' | 'negative' = 'negative';
  feedbackValidationError: boolean = false;
  private feedbackTargetMessage: ChatMessage | null = null;

  // Disclaimer modal state
  isDisclaimerModalOpen: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private realAssistService: RealAssistService
  ) {}

  ngOnInit(): void {
    this.subscribeToStreamingMessages();
  }

  ngOnDestroy(): void {
    if (this.streamingSubscription) {
      this.streamingSubscription.unsubscribe();
    }
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    // Clean up modal from body if it was moved there
    if (
      this.modalOverlay?.nativeElement?.parentElement === this.document.body
    ) {
      this.renderer.removeChild(
        this.document.body,
        this.modalOverlay.nativeElement
      );
    }
    // Restore body scroll if modal was open
    if (this.isModalOpen) {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  private subscribeToStreamingMessages(): void {
    this.streamingSubscription = this.realAssistService
      .getStreamingMessages()
      .subscribe((message: StreamingMessage) => {
        this.handleStreamingMessage(message);
      });
  }

  private handleStreamingMessage(message: StreamingMessage): void {
    if (message.error) {
      // Handle error
      this.isTyping = false;
      if (this.currentStreamingMessage) {
        // Add message to array if not already added
        if (!this.messages.includes(this.currentStreamingMessage)) {
          this.messages.push(this.currentStreamingMessage);
        }
        this.currentStreamingMessage.content =
          'I apologize, but I am having trouble connecting right now. Please try again later.';
        this.currentStreamingMessage.isStreaming = false;
        this.currentStreamingMessage = null;
      }
      this.shouldScrollToBottom = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.currentStreamingMessage) {
      const trimmedContent = message.content.trim();

      // On first content, hide typing indicator and add message to array
      if (
        trimmedContent &&
        !this.messages.includes(this.currentStreamingMessage)
      ) {
        this.isTyping = false;
        this.messages.push(this.currentStreamingMessage);
      }

      this.currentStreamingMessage.content = trimmedContent;
      this.currentStreamingMessage.responseId = message.responseId;

      if (message.isComplete) {
        this.isTyping = false; // Ensure typing is off
        this.currentStreamingMessage.isStreaming = false;
        this.currentStreamingMessage = null;
      }

      this.shouldScrollToBottom = true;
      this.cdr.markForCheck();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  onSubmit(): void {
    if (this.inputValue.trim()) {
      this.sendMessage(this.inputValue.trim());
      this.inputValue = '';
      // Reset textarea heights
      if (this.widgetInput?.nativeElement) {
        this.widgetInput.nativeElement.style.height = 'auto';
      }
      if (this.modalInput?.nativeElement) {
        this.modalInput.nativeElement.style.height = 'auto';
      }
    }
  }

  onSuggestedQuestionClick(question: RealAssistQuestion): void {
    this.suggestedQuestionClicked.emit(question);
    this.sendMessage(question.text.trim());
  }

  onActionButtonClick(button: ChatActionButton): void {
    this.sendMessage(button.label, button.value);
  }

  onInputKeydown(event: KeyboardEvent): void {
    // Enter without Shift submits the message
    // Shift+Enter creates a new line (default textarea behavior)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  autoResizeTextarea(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    // Reset height to auto to get accurate scrollHeight
    textarea.style.height = 'auto';
    textarea.style.overflow = 'hidden';

    const maxHeight = 150; // 6 lines
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = newHeight + 'px';

    // Show scrollbar if content exceeds max height
    if (textarea.scrollHeight > maxHeight) {
      textarea.style.overflow = 'auto';
    }
  }

  resetTextareaHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
  }

  onInputFocus(): void {
    // Clear any pending blur timeout
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
    this.isExpanded = true;
    this.cdr.markForCheck();
  }

  onInputBlur(): void {
    // Delay collapse to allow clicking on suggestions
    this.blurTimeout = setTimeout(() => {
      if (!this.chatStarted && !this.inputValue.trim()) {
        this.isExpanded = false;
        this.cdr.markForCheck();
      }
    }, 200);
  }

  openModal(): void {
    this.isModalOpen = true;
    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    this.cdr.markForCheck();

    // Move modal to body after it's rendered to escape any containing blocks
    setTimeout(() => {
      if (this.modalOverlay?.nativeElement) {
        this.renderer.appendChild(
          this.document.body,
          this.modalOverlay.nativeElement
        );
      }
    });
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.renderer.removeStyle(this.document.body, 'overflow');
    this.cdr.markForCheck();
  }

  private sendMessage(displayText: string, lookupKey?: string): void {
    const trimmedText = displayText.trim();
    const trimmedLookupKey = lookupKey?.trim();

    // Start chat if not already started - add greeting as first message
    if (!this.chatStarted) {
      this.chatStarted = true;

      // Add greeting as first assistant message
      const greetingMessage: ChatMessage = {
        id: this.generateId(),
        type: 'assistant',
        content:
          "Hi 👋 I'm here to help with your HOA or portal needs. What can I do for you today?",
        timestamp: new Date(),
        isGreeting: true
      };
      this.messages.push(greetingMessage);
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: this.generateId(),
      type: 'user',
      content: trimmedText,
      timestamp: new Date()
    };
    this.messages.push(userMessage);
    this.shouldScrollToBottom = true;
    this.cdr.markForCheck();

    // Emit event
    this.questionSubmitted.emit(trimmedText);

    // Request real assistant response with streaming
    this.requestStreamingResponse(trimmedLookupKey || trimmedText);
  }

  private requestStreamingResponse(query: string): void {
    this.isTyping = true;
    this.cdr.markForCheck();

    // Prepare assistant message but don't add it yet - wait for first content
    const assistantMessage: ChatMessage = {
      id: this.generateId(),
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };

    // Store reference but don't add to messages array yet
    this.currentStreamingMessage = assistantMessage;

    // Send message to Real Assist API immediately
    this.realAssistService.sendMessage(query);
  }

  private scrollToBottom(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      // Scroll widget container
      if (this.messagesContainer?.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
      // Scroll modal container
      if (this.modalMessagesContainer?.nativeElement) {
        const container = this.modalMessagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    }, 0);
  }

  private generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  resetChat(): void {
    this.messages = [];
    this.chatStarted = false;
    this.isTyping = false;
    this.currentStreamingMessage = null;
    this.realAssistService.resetSession();
    this.cdr.markForCheck();
  }

  formatTime(date: Date): string {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'p' : 'a';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes}${ampm}`;
  }

  onThumbsUp(message: ChatMessage): void {
    if (message.responseId && !message.feedbackGiven) {
      message.feedbackGiven = 'positive';
      this.cdr.markForCheck();

      this.realAssistService
        .submitCurrentSessionFeedback(message.responseId, 'positive')
        .subscribe({
          error: () => {
            // Revert feedback state on error
            message.feedbackGiven = null;
            this.cdr.markForCheck();
          }
        });
    }
  }

  onThumbsDown(message: ChatMessage): void {
    if (message.responseId && !message.feedbackGiven) {
      this.feedbackTargetMessage = message;
      this.feedbackComment = '';
      this.feedbackRating = 'negative';
      this.isFeedbackModalOpen = true;
      this.cdr.markForCheck();
    }
  }

  closeFeedbackModal(): void {
    this.isFeedbackModalOpen = false;
    this.feedbackTargetMessage = null;
    this.feedbackComment = '';
    this.feedbackValidationError = false;
    this.cdr.markForCheck();
  }

  submitFeedback(): void {
    if (!this.feedbackComment.trim()) {
      this.feedbackValidationError = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.feedbackTargetMessage?.responseId) {
      this.feedbackTargetMessage.feedbackGiven = this.feedbackRating;
      this.cdr.markForCheck();

      this.realAssistService
        .submitCurrentSessionFeedback(
          this.feedbackTargetMessage.responseId,
          this.feedbackRating,
          this.feedbackComment.trim()
        )
        .subscribe({
          error: () => {
            // Revert feedback state on error
            if (this.feedbackTargetMessage) {
              this.feedbackTargetMessage.feedbackGiven = null;
              this.cdr.markForCheck();
            }
          }
        });

      this.closeFeedbackModal();
    }
  }

  openDisclaimerModal(event: Event): void {
    event.preventDefault();
    this.isDisclaimerModalOpen = true;
    this.cdr.markForCheck();
  }

  closeDisclaimerModal(): void {
    this.isDisclaimerModalOpen = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isDisclaimerModalOpen) {
      this.closeDisclaimerModal();
    } else if (this.isFeedbackModalOpen) {
      this.closeFeedbackModal();
    }
  }

  onCopyMessage(message: ChatMessage): void {
    navigator.clipboard
      .writeText(message.content)
      .then(() => {
        this.copiedMessageId = message.id;
        this.cdr.markForCheck();

        // Clear any existing timeout
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout);
        }

        // Reset after 2 seconds
        this.copyTimeout = setTimeout(() => {
          this.copiedMessageId = null;
          this.cdr.markForCheck();
        }, 2000);
      })
      .catch(() => {
        // Fallback for older browsers or when clipboard API fails
        this.fallbackCopyToClipboard(message.content);
      });
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      this.copiedMessageId = null;
      this.cdr.markForCheck();
    } catch {
      // Silently fail if copy is not supported
    }

    document.body.removeChild(textArea);
  }
}
